import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { ArtworkCard } from '@/components/ArtworkCard'
import { SmartImage } from '@/components/SmartImage'
import { SkeletonGrid } from '@/components/states'
import { paths } from '@/lib/paths'
import { useArtworks, useCategories } from '@/lib/queries'
import { canonicalURL, personSchema, useSeo } from '@/lib/seo'
import { useLocale } from '@/lib/useLocale'

export function HomePage() {
  const locale = useLocale()
  const { t } = useTranslation()

  const categories = useCategories(locale)
  const latest = useArtworks(locale)
  const latestItems = latest.data?.pages[0]?.items.slice(0, 6) ?? []

  useSeo({
    title: `${t('site.name')} — ${t('site.tagline')}`,
    description: t('site.defaultDescription'),
    path: '/',
    locale,
    image: latestItems[0]?.cover?.variants.find((v) => v.label === 'full')?.url,
    imageAlt: latestItems[0]?.title,
    structuredData: [
      personSchema(t('site.name'), t('site.defaultDescription')),
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: t('site.name'),
        url: canonicalURL(locale, '/'),
        inLanguage: locale,
      },
    ],
  })

  return (
    <>
      <section className="container-page pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="max-w-2xl">
          <h1 className="font-[family-name:var(--font-display)] text-5xl leading-[1.05] sm:text-6xl">
            {t('home.heroTitle')}
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-stone-600">{t('home.heroSubtitle')}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to={paths.gallery(locale)} className="btn btn-primary">
              {t('home.viewGallery')}
            </Link>
            <Link to={paths.contacts(locale)} className="btn btn-secondary">
              {t('home.commission')}
            </Link>
          </div>
        </div>
      </section>

      {(categories.data?.length ?? 0) > 0 && (
        <section className="container-page pb-20">
          <header className="mb-8">
            <h2 className="text-3xl">{t('home.sectionsTitle')}</h2>
            <p className="mt-2 text-stone-500">{t('home.sectionsSubtitle')}</p>
          </header>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {categories.data?.map((category) => (
              <Link
                key={category.id}
                to={paths.category(locale, category.slug)}
                className="group relative block overflow-hidden rounded-[var(--radius-card)] bg-stone-100"
              >
                <div className="aspect-16/10">
                  <SmartImage
                    media={category.cover ?? category.icon}
                    locale={locale}
                    alt={category.title}
                    sizes="(min-width: 640px) 50vw, 100vw"
                    className="h-full w-full transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                {/* A dark wash under the caption keeps it readable whatever
                    the photo behind it happens to be. */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-stone-950/75 to-transparent p-6 pt-16">
                  <h3 className="font-[family-name:var(--font-display)] text-2xl text-white">
                    {category.title}
                  </h3>
                  {category.artworkCount > 0 && (
                    <p className="mt-1 text-sm text-stone-300">
                      {t('gallery.worksCount', { count: category.artworkCount })}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="container-page pb-8">
        <header className="mb-8 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl">{t('home.latestTitle')}</h2>
            <p className="mt-2 text-stone-500">{t('home.latestSubtitle')}</p>
          </div>
          <Link to={paths.gallery(locale)} className="link-underline shrink-0 text-sm">
            {t('home.seeAll')}
          </Link>
        </header>

        {latest.isPending ? (
          <SkeletonGrid count={6} />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {latestItems.map((artwork, index) => (
              <ArtworkCard
                key={artwork.id}
                artwork={artwork}
                locale={locale}
                priority={index < 3}
              />
            ))}
          </div>
        )}
      </section>
    </>
  )
}
